"use client"

import { useState, useEffect, useCallback } from "react"
import { motion } from "framer-motion"
import { Quote, RefreshCw, Copy } from 'lucide-react'

const QuotesGenerator = () => {
  const [quote, setQuote] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [copied, setCopied] = useState(false)

  const fetchQuote = useCallback(async () => {
    setLoading(true)
    setError(null)
    setCopied(false)

    const myHeaders = new Headers()
    myHeaders.append("x-apihub-key", "dC-X6X1n75W9ul7oEz2tOmvuaww1WLB3cGiKWsfsq057qO4-Ay")
    myHeaders.append("x-apihub-host", "Popular-Quotes-API-by-APIRobots.allthingsdev.co")
    myHeaders.append("x-apihub-endpoint", "796e8947-91ae-4181-9e93-d3677e5c0805")

    const requestOptions = {
      method: "GET",
      headers: myHeaders,
      redirect: "follow"
    }

    try {
      const response = await fetch("https://Popular-Quotes-API-by-APIRobots.proxy-production.allthingsdev.co/v1/quotes/random", requestOptions)
      const result = await response.json()
      setQuote(result)
    } catch (error) {
      console.error("Error:", error)
      setError("Failed to fetch a quote. Please try again.")
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchQuote()
  }, [fetchQuote])

  const handleCopyQuote = () => {
    if (quote) {
      const textToCopy = `"${quote.text}" - ${quote.author}`
      navigator.clipboard.writeText(textToCopy)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center mb-8"
      >
        <Quote className="inline-block text-indigo-600 mb-4" size={48} />
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white">Quotes Generator AI</h1>
        <p className="text-lg text-gray-600 dark:text-gray-300 mt-2">
          Get inspired with random quotes!
        </p>
      </motion.div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
        <div className="p-6">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <RefreshCw className="animate-spin text-indigo-600" size={48} />
            </div>
          ) : error ? (
            <div className="text-center text-red-500">{error}</div>
          ) : quote ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="text-center"
            >
              <blockquote className="text-2xl font-semibold italic text-gray-900 dark:text-white">
                "{quote.text}"
              </blockquote>
              <p className="mt-4 text-gray-600 dark:text-gray-400">- {quote.author}</p>
              <div className="mt-6 flex flex-wrap justify-center gap-2">
                {quote.categories.map((category, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-sm"
                  >
                    {category}
                  </span>
                ))}
              </div>
            </motion.div>
          ) : null}
        </div>
      </div>

      <div className="mt-8 flex justify-center space-x-4">
        <button
          onClick={fetchQuote}
          className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-50 transition-colors duration-300 flex items-center"
        >
          <RefreshCw className="mr-2" size={18} />
          New Quote
        </button>
        <button
          onClick={handleCopyQuote}
          className="px-6 py-2 bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-white rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50 transition-colors duration-300 flex items-center"
        >
          <Copy className="mr-2" size={18} />
          {copied ? "Copied!" : "Copy Quote"}
        </button>
      </div>
    </div>
  )
}

export default QuotesGenerator
